import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiHome, FiUpload, FiSave, FiCreditCard, FiPlusCircle, FiCheck, FiEdit2, FiTrash2, FiX, FiZap, FiCopy, FiExternalLink, FiBook, FiDownload, FiFileText } from 'react-icons/fi';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';

function SettingsShop() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [shopSettings, setShopSettings] = useState(null);
  const [banks, setBanks] = useState([]);
  const [shopForm, setShopForm] = useState({ shopName: '', phone: '', address: '', businessName: '', taxCode: '' });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [savingShop, setSavingShop] = useState(false);

  // Bank form state
  const [showBankModal, setShowBankModal] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [bankForm, setBankForm] = useState({ bankCode: '', bankName: '', accountNumber: '', accountName: '', isDefault: false, isPrimary: false });

  // Payment Gateway state
  const [paymentGateway, setPaymentGateway] = useState({
    enabled: false,
    provider: '',
    sepay: { apiToken: '', webhookSecret: '' },
    casso: { apiKey: '', webhookSecret: '' },
    payos: { clientId: '', apiKey: '', checksumKey: '' }
  });
  const [savingPayment, setSavingPayment] = useState(false);

  // Auto Switch Bank state
  const [autoSwitchBank, setAutoSwitchBank] = useState({
    enabled: false,
    schedules: []
  });
  const [savingAutoSwitch, setSavingAutoSwitch] = useState(false);

  // Auto Match Payment state
  const [autoMatchPayment, setAutoMatchPayment] = useState({
    enabled: true,
    disableSchedule: {
      enabled: false,
      startTime: '00:00',
      endTime: '06:00'
    }
  });
  const [savingAutoMatch, setSavingAutoMatch] = useState(false);

  // Accounting export state
  const [showExportS2aModal, setShowExportS2aModal] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadShopSettings();
    loadBanksList();
    loadAutoSwitchBank();
    loadAutoMatchPayment();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // For regular admin: Load default bank into form when shopSettings loads
  useEffect(() => {
    if (shopSettings && !shopSettings.isSuperAdmin) {
      const defaultBank = shopSettings.bankAccounts?.find(b => b.isDefault);
      if (defaultBank) {
        setBankForm({
          bankCode: defaultBank.bankCode || '',
          bankName: defaultBank.bankName || '',
          accountNumber: defaultBank.accountNumber || '',
          accountName: defaultBank.accountName || '',
          isDefault: true,
          isPrimary: defaultBank.isPrimary || false
        });
        setEditingBank(defaultBank);
      }
    }
  }, [shopSettings]);

  const loadShopSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/shop-settings`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.data.success) {
        setShopSettings(response.data.data);
        setShopForm({
          shopName: response.data.data.shopName || '',
          phone: response.data.data.phone || '',
          address: response.data.data.address || '',
          businessName: response.data.data.businessName || '',
          taxCode: response.data.data.taxCode || ''
        });
        if (response.data.data.avatar) {
          setAvatarPreview(`${API_URL.replace('/api', '')}${response.data.data.avatar}`);
        }
        // Load payment gateway settings
        if (response.data.data.paymentGateway) {
          setPaymentGateway({
            enabled: response.data.data.paymentGateway.enabled || false,
            provider: response.data.data.paymentGateway.provider || '',
            sepay: response.data.data.paymentGateway.sepay || { apiToken: '', webhookSecret: '' },
            casso: response.data.data.paymentGateway.casso || { apiKey: '', webhookSecret: '' },
            payos: response.data.data.paymentGateway.payos || { clientId: '', apiKey: '', checksumKey: '' }
          });
        }
      }
    } catch (error) {
      console.error('Error loading shop settings:', error);
    }
  };

  const loadBanksList = async () => {
    try {
      const response = await axios.get(`${API_URL}/shop-settings/banks`);
      if (response.data.success) {
        setBanks(response.data.data);
      }
    } catch (error) {
      console.error('Error loading banks:', error);
    }
  };

  const loadAutoSwitchBank = async () => {
    try {
      const response = await axios.get(`${API_URL}/shop-settings/auto-switch-bank`);
      if (response.data.success && response.data.data) {
        setAutoSwitchBank(response.data.data);
      }
    } catch (error) {
      console.error('Error loading auto switch bank:', error);
    }
  };

  const handleSaveAutoSwitchBank = async () => {
    try {
      setSavingAutoSwitch(true);
      await axios.put(`${API_URL}/shop-settings/auto-switch-bank`, autoSwitchBank);
      toast.success('Đã lưu cấu hình tự động chuyển ngân hàng');
    } catch (error) {
      toast.error('Lỗi lưu cấu hình');
    } finally {
      setSavingAutoSwitch(false);
    }
  };

  const addSchedule = () => {
    setAutoSwitchBank(prev => ({
      ...prev,
      schedules: [...prev.schedules, { time: '06:00', bankAccountNumber: '' }]
    }));
  };

  const removeSchedule = (index) => {
    setAutoSwitchBank(prev => ({
      ...prev,
      schedules: prev.schedules.filter((_, i) => i !== index)
    }));
  };

  const updateSchedule = (index, field, value) => {
    setAutoSwitchBank(prev => ({
      ...prev,
      schedules: prev.schedules.map((schedule, i) => 
        i === index ? { ...schedule, [field]: value } : schedule
      )
    }));
  };

  const loadAutoMatchPayment = async () => {
    try {
      const response = await axios.get(`${API_URL}/shop-settings/auto-match-payment`);
      if (response.data.success && response.data.data) {
        setAutoMatchPayment(response.data.data);
      }
    } catch (error) {
      console.error('Error loading auto match payment:', error);
    }
  };

  const handleSaveAutoMatchPayment = async () => {
    try {
      setSavingAutoMatch(true);
      await axios.put(`${API_URL}/shop-settings/auto-match-payment`, autoMatchPayment);
      toast.success('Đã lưu cấu hình tự động khớp thanh toán');
    } catch (error) {
      toast.error('Lỗi lưu cấu hình');
    } finally {
      setSavingAutoMatch(false);
    }
  };

  // Export S2a-HKD accounting book
  const handleExportS2a = async () => {
    if (!exportDateFrom || !exportDateTo) {
      toast.error('Vui lòng chọn khoảng thời gian');
      return;
    }

    try {
      setExporting(true);
      
      const response = await axios.get(`${API_URL}/reports/export-s2a`, {
        params: {
          from: exportDateFrom,
          to: exportDateTo
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Format filename with date range
      const fromDate = new Date(exportDateFrom).toLocaleDateString('vi-VN').replace(/\//g, '-');
      const toDate = new Date(exportDateTo).toLocaleDateString('vi-VN').replace(/\//g, '-');
      link.setAttribute('download', `So-S2a-HKD_${fromDate}_den_${toDate}.docx`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Đã xuất file thành công!');
      setShowExportS2aModal(false);
    } catch (error) {
      console.error('Error exporting S2a:', error);
      toast.error('Không thể xuất file. Vui lòng thử lại.');
    } finally {
      setExporting(false);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleSaveShop = async (e) => {
    e.preventDefault();
    try {
      setSavingShop(true);
      const formData = new FormData();
      formData.append('shopName', shopForm.shopName);
      formData.append('phone', shopForm.phone);
      formData.append('address', shopForm.address);
      formData.append('businessName', shopForm.businessName);
      formData.append('taxCode', shopForm.taxCode);
      if (avatarFile) {
        formData.append('avatar', avatarFile);
      }

      await axios.put(`${API_URL}/shop-settings`, formData);
      toast.success('Đã lưu thông tin cửa hàng');
      loadShopSettings();
    } catch (error) {
      toast.error('Không thể lưu thông tin');
    } finally {
      setSavingShop(false);
    }
  };

  // Bank account functions
  const openBankModal = (bank = null) => {
    if (bank) {
      setEditingBank(bank);
      setBankForm({
        bankCode: bank.bankCode,
        bankName: bank.bankName,
        accountNumber: bank.accountNumber,
        accountName: bank.accountName,
        isDefault: bank.isDefault,
        isPrimary: bank.isPrimary || false
      });
    } else {
      setEditingBank(null);
      setBankForm({ bankCode: '', bankName: '', accountNumber: '', accountName: '', isDefault: false, isPrimary: false });
    }
    setShowBankModal(true);
  };

  const handleBankSelect = (e) => {
    const selectedBank = banks.find(b => b.code === e.target.value);
    if (selectedBank) {
      setBankForm({ ...bankForm, bankCode: selectedBank.code, bankName: selectedBank.name });
    }
  };

  const handleSaveBank = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      if (editingBank) {
        await axios.put(`${API_URL}/shop-settings/bank-accounts/${editingBank._id}`, bankForm, config);
        toast.success('Đã cập nhật tài khoản');
      } else {
        await axios.post(`${API_URL}/shop-settings/bank-accounts`, bankForm, config);
        toast.success('Đã thêm tài khoản');
      }
      setShowBankModal(false);
      loadShopSettings();
    } catch (error) {
      toast.error('Không thể lưu tài khoản');
    }
  };

  const handleDeleteBank = async (id) => {
    if (!window.confirm('Xóa tài khoản này?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      await axios.delete(`${API_URL}/shop-settings/bank-accounts/${id}`, config);
      toast.success('Đã xóa tài khoản');
      loadShopSettings();
    } catch (error) {
      toast.error('Không thể xóa tài khoản');
    }
  };

  const handleSetDefaultBank = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      await axios.put(`${API_URL}/shop-settings/bank-accounts/${id}`, { isDefault: true }, config);
      toast.success('Đã đặt làm mặc định');
      loadShopSettings();
    } catch (error) {
      toast.error('Không thể cập nhật');
    }
  };

  const handleSavePaymentGateway = async () => {
    try {
      setSavingPayment(true);
      await axios.put(`${API_URL}/shop-settings/payment-gateway`, paymentGateway);
      toast.success('Đã lưu cài đặt thanh toán');
      loadShopSettings();
    } catch (error) {
      toast.error('Không thể lưu cài đặt');
    } finally {
      setSavingPayment(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Đã sao chép');
  };

  const getWebhookUrl = () => {
    const baseUrl = API_URL.replace('/api', '');
    return `${baseUrl}/api/webhook/payment/${paymentGateway.provider}`;
  };

  return (
    <div className={`space-y-6 ${isMobile ? 'pb-20' : ''}`}>
      <h1 className="text-2xl font-bold text-gray-800">Cài đặt cửa hàng</h1>

      {/* Shop Info */}
      <div className="bg-white rounded-lg border p-4 md:p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center">
          <FiHome className="mr-2 text-green-600" />
          Thông tin cửa hàng / Hộ kinh doanh
        </h3>
        
        <form onSubmit={handleSaveShop} className="space-y-4">
          {/* Logo */}
          <div className="flex items-center space-x-4">
            <div className="relative">
              {avatarPreview ? (
                <img src={avatarPreview} alt="Logo" className="w-20 h-20 rounded-lg object-cover border" />
              ) : (
                <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center border">
                  <FiHome className="text-gray-400" size={32} />
                </div>
              )}
            </div>
            <div>
              <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg inline-flex items-center">
                <FiUpload className="mr-2" />
                Tải logo
                <input type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
              </label>
              <p className="text-xs text-gray-500 mt-1">Tối đa 5MB, định dạng JPG/PNG</p>
            </div>
          </div>

          {/* Tên cửa hàng */}
          <div>
            <label className="block font-medium mb-1">Tên cửa hàng</label>
            <input
              type="text"
              value={shopForm.shopName}
              onChange={(e) => setShopForm({ ...shopForm, shopName: e.target.value })}
              className="w-full border rounded-lg px-4 py-2"
              placeholder="Nhập tên cửa hàng"
            />
          </div>

          {/* Số điện thoại */}
          <div>
            <label className="block font-medium mb-1">Số điện thoại</label>
            <input
              type="tel"
              value={shopForm.phone}
              onChange={(e) => setShopForm({ ...shopForm, phone: e.target.value })}
              className="w-full border rounded-lg px-4 py-2"
              placeholder="Nhập số điện thoại"
            />
          </div>

          {/* Hộ, cá nhân kinh doanh */}
          <div>
            <label className="block font-medium mb-1">Hộ, cá nhân kinh doanh</label>
            <input
              type="text"
              value={shopForm.businessName}
              onChange={(e) => setShopForm({ ...shopForm, businessName: e.target.value })}
              className="w-full border rounded-lg px-4 py-2"
              placeholder="Nhập tên hộ/cá nhân kinh doanh"
            />
          </div>

          {/* Mã số thuế */}
          <div>
            <label className="block font-medium mb-1">Mã số thuế</label>
            <input
              type="text"
              value={shopForm.taxCode}
              onChange={(e) => setShopForm({ ...shopForm, taxCode: e.target.value })}
              className="w-full border rounded-lg px-4 py-2"
              placeholder="Nhập mã số thuế"
            />
          </div>

          {/* Địa điểm kinh doanh */}
          <div>
            <label className="block font-medium mb-1">Địa điểm kinh doanh</label>
            <input
              type="text"
              value={shopForm.address}
              onChange={(e) => setShopForm({ ...shopForm, address: e.target.value })}
              className="w-full border rounded-lg px-4 py-2"
              placeholder="Nhập địa điểm kinh doanh"
            />
          </div>

          <button
            type="submit"
            disabled={savingShop}
            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
          >
            <FiSave className="mr-2" />
            {savingShop ? 'Đang lưu...' : 'Lưu thông tin'}
          </button>
        </form>
      </div>

      {/* Accounting Book - Sổ kế toán */}
      <div className="bg-white rounded-lg border p-4 md:p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center">
          <FiBook className="mr-2 text-blue-600" />
          Sổ kế toán
        </h3>
        
        <p className="text-sm text-gray-600 mb-4">
          Xuất các mẫu sổ kế toán theo quy định của Bộ Tài chính (Thông tư 152/2025/TT-BTC)
        </p>

        {/* List of accounting templates */}
        <div className="space-y-3">
          {/* Template S2a-HKD */}
          <div 
            onClick={() => {
              // Set default date range (current month)
              const now = new Date();
              const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
              const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
              setExportDateFrom(firstDay.toISOString().split('T')[0]);
              setExportDateTo(lastDay.toISOString().split('T')[0]);
              setShowExportS2aModal(true);
            }}
            className="border rounded-lg p-4 hover:border-green-500 hover:bg-green-50 cursor-pointer transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <FiFileText className="text-green-600" />
                  <span className="font-medium">Mẫu S2a-HKD: SỔ DOANH THU BÁN HÀNG HÓA, DỊCH VỤ</span>
                </div>
                <p className="text-sm text-gray-500 mt-1 ml-6">
                  Dành cho hộ kinh doanh có doanh thu từ 500 triệu - 3 tỷ/năm, nộp thuế GTGT và TNCN theo tỷ lệ % trên doanh thu
                </p>
              </div>
              <FiDownload className="text-gray-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Bank Accounts */}
      <div className="bg-white rounded-lg border p-4 md:p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-lg flex items-center">
            <FiCreditCard className="mr-2 text-blue-600" />
            Tài khoản ngân hàng
          </h3>
          {/* Only super admin can add new bank accounts */}
          {shopSettings?.isSuperAdmin && (
            <button
              onClick={() => openBankModal()}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
            >
              <FiPlusCircle className="mr-2" /> Thêm tài khoản
            </button>
          )}
        </div>

        {/* Super Admin: Show all bank accounts */}
        {shopSettings?.isSuperAdmin ? (
          <>
            {shopSettings?.bankAccounts?.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Chưa có tài khoản ngân hàng nào</p>
            ) : (
              <div className="space-y-3">
                {shopSettings?.bankAccounts?.map(bank => (
                  <div key={bank._id} className={`border rounded-lg p-4 flex justify-between items-center ${bank.isPrimary ? 'border-purple-300 bg-purple-50' : ''}`}>
                    <div>
                      <div className="flex items-center space-x-2 flex-wrap gap-1">
                        <span className="font-bold">{bank.bankName}</span>
                        {bank.isPrimary && (
                          <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded">Tài khoản chính</span>
                        )}
                        {bank.isDefault && (
                          <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded">Mặc định</span>
                        )}
                      </div>
                      <p className="text-gray-600">{bank.accountNumber}</p>
                      <p className="text-gray-500 text-sm">{bank.accountName}</p>
                    </div>
                    <div className="flex space-x-2">
                      {!bank.isDefault && (
                        <button onClick={() => handleSetDefaultBank(bank._id)} className="text-green-600 hover:bg-green-50 p-2 rounded" title="Đặt làm mặc định">
                          <FiCheck />
                        </button>
                      )}
                      <button onClick={() => openBankModal(bank)} className="text-blue-600 hover:bg-blue-50 p-2 rounded">
                        <FiEdit2 />
                      </button>
                      <button onClick={() => handleDeleteBank(bank._id)} className="text-red-600 hover:bg-red-50 p-2 rounded">
                        <FiTrash2 />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          /* Regular Admin: Show only default bank with inline edit form */
          <div className="space-y-4">
            {(() => {
              const defaultBank = shopSettings?.bankAccounts?.find(b => b.isDefault);
              if (!defaultBank) {
                return <p className="text-gray-500 text-center py-8">Chưa có tài khoản ngân hàng mặc định</p>;
              }
              return (
                <form onSubmit={handleSaveBank} className="space-y-4">
                  <div>
                    <label className="block font-semibold mb-2">Ngân hàng *</label>
                    <select
                      value={bankForm.bankCode}
                      onChange={handleBankSelect}
                      className="w-full border rounded-lg px-4 py-2"
                      required
                    >
                      <option value="">-- Chọn ngân hàng --</option>
                      {banks.map(bank => (
                        <option key={bank.code} value={bank.code}>{bank.name} ({bank.code})</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block font-semibold mb-2">Số tài khoản *</label>
                    <input
                      type="text"
                      value={bankForm.accountNumber}
                      onChange={(e) => setBankForm({ ...bankForm, accountNumber: e.target.value })}
                      className="w-full border rounded-lg px-4 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block font-semibold mb-2">Tên chủ tài khoản *</label>
                    <input
                      type="text"
                      value={bankForm.accountName}
                      onChange={(e) => setBankForm({ ...bankForm, accountName: e.target.value.toUpperCase() })}
                      className="w-full border rounded-lg px-4 py-2"
                      required
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 flex items-center"
                  >
                    <FiSave className="mr-2" /> Lưu thay đổi
                  </button>
                </form>
              );
            })()}
          </div>
        )}
      </div>

      {/* Bank Modal - Only for Super Admin */}
      {showBankModal && shopSettings?.isSuperAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{editingBank ? 'Sửa tài khoản' : 'Thêm tài khoản ngân hàng'}</h2>
              <button onClick={() => setShowBankModal(false)} className="text-gray-400 hover:text-gray-600">
                <FiX size={24} />
              </button>
            </div>
            <form onSubmit={handleSaveBank} className="space-y-4">
              <div>
                <label className="block font-semibold mb-2">Ngân hàng *</label>
                <select
                  value={bankForm.bankCode}
                  onChange={handleBankSelect}
                  className="w-full border rounded-lg px-4 py-2"
                  required
                >
                  <option value="">-- Chọn ngân hàng --</option>
                  {banks.map(bank => (
                    <option key={bank.code} value={bank.code}>{bank.name} ({bank.code})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-semibold mb-2">Số tài khoản *</label>
                <input
                  type="text"
                  value={bankForm.accountNumber}
                  onChange={(e) => setBankForm({ ...bankForm, accountNumber: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2"
                  required
                />
              </div>

              <div>
                <label className="block font-semibold mb-2">Tên chủ tài khoản *</label>
                <input
                  type="text"
                  value={bankForm.accountName}
                  onChange={(e) => setBankForm({ ...bankForm, accountName: e.target.value.toUpperCase() })}
                  className="w-full border rounded-lg px-4 py-2"
                  required
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={bankForm.isDefault}
                  onChange={(e) => setBankForm({ ...bankForm, isDefault: e.target.checked })}
                />
                <label htmlFor="isDefault">Đặt làm tài khoản mặc định</label>
              </div>

              {/* isPrimary - chỉ hiển thị cho super admin */}
              {shopSettings?.isSuperAdmin && (
                <div className="flex items-center space-x-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <input
                    type="checkbox"
                    id="isPrimary"
                    checked={bankForm.isPrimary}
                    onChange={(e) => setBankForm({ ...bankForm, isPrimary: e.target.checked })}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="isPrimary" className="text-purple-800">
                    <span className="font-medium">Tài khoản chính</span>
                    <span className="block text-xs text-purple-600">Chỉ super admin mới thấy tài khoản này</span>
                  </label>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button type="button" onClick={() => setShowBankModal(false)} className="px-4 py-2 border rounded-lg">
                  Hủy
                </button>
                <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">
                  {editingBank ? 'Cập nhật' : 'Thêm'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Auto Switch Bank Section - Only for Super Admin */}
      {shopSettings?.isSuperAdmin && (
        <div className="bg-white rounded-lg border p-4 md:p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center">
            <FiCreditCard className="mr-2 text-purple-500" />
            Tự động chuyển ngân hàng mặc định
          </h3>

          <div className="mb-4 p-3 bg-purple-50 rounded-lg text-sm text-purple-800">
            <p>Tự động chuyển ngân hàng mặc định vào các mốc thời gian cố định hàng ngày.</p>
          </div>

          {/* Enable/Disable */}
          <div className="mb-4">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={autoSwitchBank.enabled}
                onChange={(e) => setAutoSwitchBank(prev => ({ ...prev, enabled: e.target.checked }))}
                className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
              />
              <span className="font-medium">Bật tự động chuyển</span>
            </label>
          </div>

          {autoSwitchBank.enabled && (
            <>
              {/* Schedules */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-gray-700">Lịch chuyển đổi</p>
                  <button
                    type="button"
                    onClick={addSchedule}
                    className="text-sm text-purple-600 hover:text-purple-700 flex items-center"
                  >
                    <FiPlusCircle className="mr-1" /> Thêm mốc thời gian
                  </button>
                </div>

                {autoSwitchBank.schedules?.length === 0 && (
                  <p className="text-sm text-gray-500 italic">Chưa có mốc thời gian nào. Nhấn "Thêm mốc thời gian" để tạo.</p>
                )}

                {autoSwitchBank.schedules?.map((schedule, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                    <div className="flex-shrink-0">
                      <label className="block text-xs text-gray-500 mb-1">Thời gian</label>
                      <input
                        type="time"
                        value={schedule.time}
                        onChange={(e) => updateSchedule(index, 'time', e.target.value)}
                        className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 w-28"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Ngân hàng</label>
                      <select
                        value={schedule.bankAccountNumber}
                        onChange={(e) => updateSchedule(index, 'bankAccountNumber', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">-- Chọn tài khoản --</option>
                        {shopSettings?.bankAccounts?.map((bank, i) => (
                          <option key={i} value={bank.accountNumber}>
                            {bank.bankName} - {bank.accountNumber} ({bank.accountName})
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSchedule(index)}
                      className="flex-shrink-0 text-red-500 hover:text-red-700 p-2"
                    >
                      <FiTrash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Info */}
              <div className="mb-4 p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
                <p className="font-medium mb-1">Ví dụ:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>06:00 → MB Bank: Mỗi ngày lúc 6h sáng sẽ set MB Bank làm mặc định</li>
                  <li>18:00 → Techcombank: Mỗi ngày lúc 6h tối sẽ set Techcombank làm mặc định</li>
                </ul>
              </div>
            </>
          )}

          <button
            type="button"
            onClick={handleSaveAutoSwitchBank}
            disabled={savingAutoSwitch}
            className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center"
          >
            <FiSave className="mr-2" />
            {savingAutoSwitch ? 'Đang lưu...' : 'Lưu cấu hình'}
          </button>
        </div>
      )}

      {/* Auto Match Payment Section - Only for Super Admin */}
      {shopSettings?.isSuperAdmin && (
        <div className="bg-white rounded-lg border p-4 md:p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center">
            <FiCheck className="mr-2 text-blue-500" />
            Tự động khớp đơn hàng theo số tiền
          </h3>

          <div className="mb-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
            <p>Khi nhận webhook thanh toán, hệ thống sẽ tự động tìm đơn hàng có số tiền khớp để cập nhật trạng thái thanh toán.</p>
          </div>

          {/* Enable/Disable */}
          <div className="mb-4">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={autoMatchPayment.enabled}
                onChange={(e) => setAutoMatchPayment(prev => ({ ...prev, enabled: e.target.checked }))}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="font-medium">Bật tự động khớp theo số tiền</span>
            </label>
          </div>

          {autoMatchPayment.enabled && (
            <>
              {/* Disable Schedule */}
              <div className="mb-4 p-4 border rounded-lg bg-gray-50">
                <label className="flex items-center space-x-3 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={autoMatchPayment.disableSchedule?.enabled}
                    onChange={(e) => setAutoMatchPayment(prev => ({
                      ...prev,
                      disableSchedule: { ...prev.disableSchedule, enabled: e.target.checked }
                    }))}
                    className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500"
                  />
                  <span className="font-medium">Tự động tắt theo khung giờ</span>
                </label>

                {autoMatchPayment.disableSchedule?.enabled && (
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Tắt từ</label>
                      <input
                        type="time"
                        value={autoMatchPayment.disableSchedule?.startTime || '00:00'}
                        onChange={(e) => setAutoMatchPayment(prev => ({
                          ...prev,
                          disableSchedule: { ...prev.disableSchedule, startTime: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Đến</label>
                      <input
                        type="time"
                        value={autoMatchPayment.disableSchedule?.endTime || '06:00'}
                        onChange={(e) => setAutoMatchPayment(prev => ({
                          ...prev,
                          disableSchedule: { ...prev.disableSchedule, endTime: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="mb-4 p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
                <p className="font-medium mb-1">Lưu ý:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Tính năng này chỉ ảnh hưởng đến việc tìm đơn theo <b>số tiền khớp</b></li>
                  <li>Tìm theo <b>mã đơn hàng</b> hoặc <b>số bàn</b> trong nội dung CK vẫn hoạt động bình thường</li>
                  <li>Ví dụ: Tắt từ 00:00 - 06:00 để tránh khớp nhầm đơn khi cửa hàng đóng cửa</li>
                </ul>
              </div>
            </>
          )}

          <button
            type="button"
            onClick={handleSaveAutoMatchPayment}
            disabled={savingAutoMatch}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            <FiSave className="mr-2" />
            {savingAutoMatch ? 'Đang lưu...' : 'Lưu cấu hình'}
          </button>
        </div>
      )}

      {/* Payment Gateway Settings */}
      <div className="bg-white rounded-lg border p-4 md:p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center">
          <FiZap className="mr-2 text-yellow-500" />
          Tích hợp thanh toán tự động
        </h3>

        <div className="mb-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
          <p>Tích hợp với SePay, Casso hoặc PayOS để tự động cập nhật trạng thái đơn hàng khi khách chuyển khoản.</p>
        </div>

        {/* Enable/Disable */}
        <div className="mb-4">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={paymentGateway.enabled}
              onChange={(e) => setPaymentGateway({ ...paymentGateway, enabled: e.target.checked })}
              className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
            />
            <span className="font-medium">Bật tích hợp thanh toán</span>
          </label>
        </div>

        {paymentGateway.enabled && (
          <>
            {/* Provider Selection */}
            <div className="mb-4">
              <label className="block font-medium mb-2">Nhà cung cấp</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'sepay', label: 'SePay', color: 'blue' },
                  { value: 'casso', label: 'Casso', color: 'purple' },
                  { value: 'payos', label: 'PayOS', color: 'green' }
                ].map(provider => (
                  <button
                    key={provider.value}
                    type="button"
                    onClick={() => setPaymentGateway({ ...paymentGateway, provider: provider.value })}
                    className={`px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                      paymentGateway.provider === provider.value
                        ? `border-${provider.color}-500 bg-${provider.color}-50 text-${provider.color}-700`
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {provider.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Provider-specific config */}
            {paymentGateway.provider === 'sepay' && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-blue-700 flex items-center">
                  Cài đặt SePay
                  <a href="https://my.sepay.vn" target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-500 hover:underline">
                    <FiExternalLink size={14} />
                  </a>
                </h4>
                
                <div className="p-3 bg-blue-50 rounded-lg text-sm">
                  <p className="font-medium mb-2">Hướng dẫn cài đặt:</p>
                  <ol className="list-decimal list-inside space-y-1 text-blue-800">
                    <li>Đăng nhập vào <a href="https://my.sepay.vn" target="_blank" rel="noopener noreferrer" className="underline">my.sepay.vn</a></li>
                    <li>Vào mục <strong>Tích hợp Webhooks → Thêm Webhooks</strong></li>
                    <li>Chọn tài khoản ngân hàng nhận thanh toán</li>
                    <li>Dán Webhook URL bên dưới vào ô "Gọi đến URL"</li>
                    <li>Kiểu chứng thực: chọn <strong>API Key</strong> và nhập API Token bên dưới</li>
                  </ol>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">API Token (dùng để xác thực webhook)</label>
                  <input
                    type="text"
                    value={paymentGateway.sepay.apiToken}
                    onChange={(e) => setPaymentGateway({
                      ...paymentGateway,
                      sepay: { ...paymentGateway.sepay, apiToken: e.target.value }
                    })}
                    className="w-full border rounded-lg px-4 py-2"
                    placeholder="Nhập một chuỗi bí mật bất kỳ (VD: my-secret-key-123)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Tự tạo một chuỗi bí mật và nhập vào đây, sau đó dùng cùng chuỗi này khi tạo webhook trên SePay
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Webhook URL (sao chép và dán vào SePay)</label>
                  <div className="flex">
                    <input
                      type="text"
                      value={getWebhookUrl()}
                      readOnly
                      className="flex-1 border rounded-l-lg px-4 py-2 bg-gray-100 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => copyToClipboard(getWebhookUrl())}
                      className="px-4 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700"
                    >
                      <FiCopy />
                    </button>
                  </div>
                </div>

                <div className="p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
                  <p className="font-medium">Lưu ý về nội dung chuyển khoản:</p>
                  <p>Hệ thống sẽ tự động nhận diện đơn hàng dựa trên số tiền + nội dung chuyển khoản.</p>
                  <p className="mt-1">Nội dung nên chứa một trong các mẫu:</p>
                  <ul className="list-disc list-inside mt-1">
                    <li><code className="bg-yellow-100 px-1">DH123</code> - Mã đơn hàng</li>
                    <li><code className="bg-yellow-100 px-1">TT Ban5</code> hoặc <code className="bg-yellow-100 px-1">B5</code> - Số bàn</li>
                  </ul>
                </div>
              </div>
            )}

            {paymentGateway.provider === 'casso' && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-purple-700 flex items-center">
                  Cài đặt Casso
                  <a href="https://casso.vn" target="_blank" rel="noopener noreferrer" className="ml-2 text-purple-500 hover:underline">
                    <FiExternalLink size={14} />
                  </a>
                </h4>
                
                <div>
                  <label className="block text-sm font-medium mb-1">API Key</label>
                  <input
                    type="text"
                    value={paymentGateway.casso.apiKey}
                    onChange={(e) => setPaymentGateway({
                      ...paymentGateway,
                      casso: { ...paymentGateway.casso, apiKey: e.target.value }
                    })}
                    className="w-full border rounded-lg px-4 py-2"
                    placeholder="Nhập API Key từ Casso"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Secure Token (Webhook)</label>
                  <input
                    type="text"
                    value={paymentGateway.casso.webhookSecret}
                    onChange={(e) => setPaymentGateway({
                      ...paymentGateway,
                      casso: { ...paymentGateway.casso, webhookSecret: e.target.value }
                    })}
                    className="w-full border rounded-lg px-4 py-2"
                    placeholder="Nhập Secure Token"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Webhook URL (cài đặt trên Casso)</label>
                  <div className="flex">
                    <input
                      type="text"
                      value={getWebhookUrl()}
                      readOnly
                      className="flex-1 border rounded-l-lg px-4 py-2 bg-gray-100 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => copyToClipboard(getWebhookUrl())}
                      className="px-4 py-2 bg-purple-600 text-white rounded-r-lg hover:bg-purple-700"
                    >
                      <FiCopy />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {paymentGateway.provider === 'payos' && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-green-700 flex items-center">
                  Cài đặt PayOS
                  <a href="https://payos.vn" target="_blank" rel="noopener noreferrer" className="ml-2 text-green-500 hover:underline">
                    <FiExternalLink size={14} />
                  </a>
                </h4>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Client ID</label>
                  <input
                    type="text"
                    value={paymentGateway.payos.clientId}
                    onChange={(e) => setPaymentGateway({
                      ...paymentGateway,
                      payos: { ...paymentGateway.payos, clientId: e.target.value }
                    })}
                    className="w-full border rounded-lg px-4 py-2"
                    placeholder="Nhập Client ID"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">API Key</label>
                  <input
                    type="text"
                    value={paymentGateway.payos.apiKey}
                    onChange={(e) => setPaymentGateway({
                      ...paymentGateway,
                      payos: { ...paymentGateway.payos, apiKey: e.target.value }
                    })}
                    className="w-full border rounded-lg px-4 py-2"
                    placeholder="Nhập API Key"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Checksum Key</label>
                  <input
                    type="text"
                    value={paymentGateway.payos.checksumKey}
                    onChange={(e) => setPaymentGateway({
                      ...paymentGateway,
                      payos: { ...paymentGateway.payos, checksumKey: e.target.value }
                    })}
                    className="w-full border rounded-lg px-4 py-2"
                    placeholder="Nhập Checksum Key"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Webhook URL (cài đặt trên PayOS)</label>
                  <div className="flex">
                    <input
                      type="text"
                      value={getWebhookUrl()}
                      readOnly
                      className="flex-1 border rounded-l-lg px-4 py-2 bg-gray-100 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => copyToClipboard(getWebhookUrl())}
                      className="px-4 py-2 bg-green-600 text-white rounded-r-lg hover:bg-green-700"
                    >
                      <FiCopy />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* How it works */}
            <div className="mt-4 p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
              <p className="font-medium mb-1">Cách hoạt động:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Khi khách chuyển khoản, hệ thống sẽ nhận thông báo từ {paymentGateway.provider || 'nhà cung cấp'}</li>
                <li>Nội dung chuyển khoản chứa số bàn (VD: "TT Ban5") sẽ được tự động khớp với đơn hàng</li>
                <li>Đơn hàng sẽ được cập nhật trạng thái "Hoàn thành" và ghi nhận thanh toán chuyển khoản</li>
              </ul>
            </div>
          </>
        )}

        <button
          type="button"
          onClick={handleSavePaymentGateway}
          disabled={savingPayment}
          className="mt-4 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
        >
          <FiSave className="mr-2" />
          {savingPayment ? 'Đang lưu...' : 'Lưu cài đặt thanh toán'}
        </button>
      </div>

      {/* Export S2a Modal */}
      {showExportS2aModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-lg">Xuất Sổ doanh thu bán hàng</h3>
              <button onClick={() => setShowExportS2aModal(false)} className="text-gray-500 hover:text-gray-700">
                <FiX size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  <strong>Mẫu S2a-HKD</strong> theo Thông tư 152/2025/TT-BTC
                </p>
                <p className="text-xs text-green-700 mt-1">
                  Dành cho hộ kinh doanh nộp thuế GTGT và TNCN theo tỷ lệ % trên doanh thu
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Từ ngày</label>
                  <input
                    type="date"
                    value={exportDateFrom}
                    onChange={(e) => setExportDateFrom(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Đến ngày</label>
                  <input
                    type="date"
                    value={exportDateTo}
                    onChange={(e) => setExportDateTo(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Thông tin sẽ được điền tự động:</strong></p>
                <ul className="list-disc list-inside text-xs space-y-0.5">
                  <li>Tên hộ kinh doanh: {shopSettings?.businessName || shopSettings?.shopName || '(Chưa cài đặt)'}</li>
                  <li>Địa chỉ: {shopSettings?.address || '(Chưa cài đặt)'}</li>
                  <li>Mã số thuế: {shopSettings?.taxCode || '(Chưa cài đặt)'}</li>
                  <li>Doanh thu theo ngày và tự động tính thuế</li>
                </ul>
              </div>

              {/* Important note about tax calculation */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs font-medium text-blue-800 mb-2">📋 Lưu ý quan trọng về cách tính thuế 2026:</p>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• <strong>Thuế GTGT:</strong> Tính trên toàn bộ doanh thu × tỷ lệ % (mặc định 5%)</li>
                  <li>• <strong>Thuế TNCN:</strong> Chỉ tính khi doanh thu năm dự kiến &gt; 500 triệu (mặc định 1.5%)</li>
                </ul>
                <div className="mt-2 pt-2 border-t border-blue-200 text-xs text-blue-600">
                  <p className="font-medium mb-1">Công thức tính thuế TNCN:</p>
                  <ol className="list-decimal list-inside space-y-0.5 text-blue-600">
                    <li>DT trung bình/ngày = Tổng DT kỳ ÷ Số ngày trong kỳ</li>
                    <li>DT năm dự kiến = DT TB/ngày × 300 ngày KD/năm</li>
                    <li>Nếu DT năm &gt; 500tr: Tỷ lệ chịu thuế = (DT năm - 500tr) ÷ DT năm</li>
                    <li>Thuế TNCN = DT kỳ × Tỷ lệ chịu thuế × Tỷ lệ % TNCN</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="p-4 border-t flex gap-3">
              <button
                onClick={() => setShowExportS2aModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={handleExportS2a}
                disabled={exporting || !exportDateFrom || !exportDateTo}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
              >
                {exporting ? (
                  <>
                    <FiDownload className="mr-2 animate-spin" />
                    Đang xuất...
                  </>
                ) : (
                  <>
                    <FiDownload className="mr-2" />
                    Xuất file Word
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsShop;
